import {
  Collapsable,
  Column,
  Container,
  RichText,
  Spacer,
  Text,
  Title
} from '@smuchile/pkg-unimarc-components'
import { isValidArrayWithData, useSession } from '@smuchile/pkg-unimarc-hooks'
import { ClubBlockProps } from './ClubBlock'
import {
  BigScreen,
  SmallScreen,
  getBemId
} from '@smuchile/pkg-unimarc-components/helpers'
import styles from './ClubBlockFaq.module.css'

export type ClubBlockFaqProps = ClubBlockProps & {
  hideContentBorder?: boolean
}

export const ClubBlockFaq = ({
  description,
  hideContentBorder = false,
  items
}: ClubBlockFaqProps) => {
  const { isLoggedIn } = useSession()

  const getItemImage = (item, isMobile: boolean) => {
    const image =
      item?.[isMobile ? 'imageMobile' : 'imageDesktop'] ||
      item?.['fields']?.[isMobile ? 'imageMobile' : 'imageDesktop'] ||
      item?.[isMobile ? 'imageDesktop' : 'imageMobile'] ||
      item?.['fields']?.[isMobile ? 'imageDesktop' : 'imageMobile']

    return image?.['fields']
  }

  const renderRichtext = (item, isMobile: boolean) => {
    const image = getItemImage(item, isMobile)
    const richtext = [item?.['value']?.['content']]

    return (
      <Column
        alignItems='center'
        customClassName={
          isMobile
            ? styles['clubBlockFaq__richText--mobile']
            : styles['clubBlockFaq__richText']
        }
      >
        {image?.['file']?.['url'] && (
          <img
            alt={image?.['description'] || image?.['title'] || item?.['name']}
            className={styles['clubBlockFaq__image']}
            loading='lazy'
            src={image['file']['url']}
          />
        )}
        <RichText richTextData={richtext} />
      </Column>
    )
  }

  return (
    <Container>
      <BigScreen>
        <Column>
          {description && (
            <Column>
              <Spacer.Horizontal size={24} />

              {isLoggedIn ? (
                <Text
                  fontSize='2xl'
                  fontWeight='semibold'
                  id={getBemId('clubBlockFaq', 'title')}
                >
                  {description}
                </Text>
              ) : (
                <Title
                  fontWeight='medium'
                  headingLevel='h1'
                  id={getBemId('clubBlockFaq', 'title')}
                >
                  {description}
                </Title>
              )}

              <Spacer.Horizontal size={24} />
            </Column>
          )}

          {isValidArrayWithData(items) &&
            items.map((item) => {
              const richtext = renderRichtext(item, false) as unknown

              return (
                <Column key={item?.['sys']?.['id']}>
                  <Collapsable
                    backgroundColor='var(--color-neutral-gray-white)'
                    dynamicIcon
                    hideContentBorder={hideContentBorder}
                    iconName='DropDownCloseV2'
                    maxHeight='none'
                    text={richtext as HTMLElement}
                    textPadding='0px 24px'
                    title={item?.['name']}
                  />
                </Column>
              )
            })}
        </Column>
      </BigScreen>

      <SmallScreen>
        <Column gap={16}>
          {description && (
            <Text
              fontSize='lg'
              fontWeight='medium'
            >
              {description}
            </Text>
          )}

          <Column>
            {isValidArrayWithData(items) &&
              items.map((item) => {
                const richtext = renderRichtext(item, true) as unknown

                return (
                  <Column key={item?.['sys']?.['id']}>
                    <Collapsable
                      backgroundColor='var(--color-neutral-gray-white)'
                      bottomSpace={16}
                      dynamicIcon
                      hideContentBorder={hideContentBorder}
                      iconName='DropDownCloseV2'
                      maxHeight='none'
                      text={richtext as HTMLElement}
                      textPadding='0px 24px'
                      title={item?.['name']}
                      titleContainerHeight='44px'
                      titleFontWeight='regular'
                    />
                  </Column>
                )
              })}
          </Column>
        </Column>
      </SmallScreen>
    </Container>
  )
}
